import Image from 'next/image';
import { createPortal } from 'react-dom';
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { quarterConverter } from '../../helpers';
import BuildingProgressSelect from './BuildingProgressSelect';
import Button from '../Button';
import styles from '../../styles/modules/building/BuildingProgress.module.scss';

const dateToText = (d) => {
  const inDate = new Date(d);

  const getShortMonth = (m) => {
    switch (m) {
      case 0:
        return 'Январь';

      case 1:
        return 'Февраль';

      case 2:
        return 'Март';

      case 3:
        return 'Апрель';

      case 4:
        return 'Май';

      case 5:
        return 'Июнь';

      case 6:
        return 'Июль';

      case 7:
        return 'Август';

      case 8:
        return 'Сентябрь';

      case 9:
        return 'Октябрь';

      case 10:
        return 'Ноябрь';

      case 11:
        return 'Декабрь';

      default:
        return '';
    }
  };

  return `${getShortMonth(inDate.getMonth())} ${inDate.getFullYear()}`;
};

const BuildingProgress = ({ progress }) => {
  const [isExpanded, setExpanded] = useState(false);
  const [clickedImage, setClickedImage] = useState(null);
  const [activeState, setActiveState] = useState(0);
  const [isFullShown, setFullShown] = useState(false);

  const handleImageClick = (url, text, index) => {
    setClickedImage({ url, text, index });
    setExpanded(true);
  };
  const handleScroll = () => {
    if (isExpanded) setExpanded(false);
  };
  const closeOnEscapeKeyDown = (e) => {
    if ((e.charCode || e.keyCode) === 27) setExpanded(false);
  };

  useEffect(() => {
    document.addEventListener('scroll', handleScroll);
    document.addEventListener('touchmove', handleScroll);
    document.addEventListener('keydown', closeOnEscapeKeyDown);

    return () => {
      document.removeEventListener('scroll', handleScroll);
      document.removeEventListener('touchmove', handleScroll);
      document.removeEventListener('keydown', closeOnEscapeKeyDown);
    };
  }, [isExpanded]);

  return (
    <section className="block">
      {createPortal(
        <AnimatePresence>
          {isExpanded && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className={styles.preview}
              role="button"
              tabIndex={0}
              onClick={() => setExpanded(false)}
            >
              <div className={styles.previewBackground} />
              {clickedImage.text && (
                <div className={styles.previewTextWrapper}>
                  <div className={styles.previewText}>{clickedImage.text}</div>
                </div>
              )}
              <motion.img
                initial={{ opacity: 0, scale: 0.5 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: 0.1, duration: 0.3 }}
                src={clickedImage.url}
                className={styles.previewImage}
                alt={clickedImage.text}
              />
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
      <div className="fixed">
        <h3 className={styles.title}>
          Ход строительства
          <BuildingProgressSelect
            content={progress.map((value, index) => {
              const opt = {
                text: `${quarterConverter(value.quartal.split('-')[0])} квартал ${
                  value.quartal.split('-')[1]
                }`,
                value: index,
                selected: index === 0
              };

              return opt;
            })}
            onSelect={(newValue) => setActiveState(newValue)}
          />
        </h3>
        <div className={styles.gallery}>
          {progress[activeState].images
            .slice(0, isFullShown ? progress[activeState].images.length : 8)
            .map((value, index) => (
              <div
                className={styles.image}
                role="button"
                tabIndex={0}
                onClick={() => handleImageClick(value.full, dateToText(progress[activeState].date))}
                key={index}
              >
                <Image
                  src={value.thumb}
                  alt={dateToText(progress[activeState].date)}
                  blurDataURL={value.blur}
                  fill
                  placeholder="blur"
                  style={{ objectFit: 'cover' }}
                />
                <div className={styles.imageDescription}>
                  {dateToText(progress[activeState].date)}
                </div>
              </div>
            ))}
        </div>
        {progress[activeState].images.length > 8 && !isFullShown && (
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 18 }}>
            <Button style={{ width: 200 }} onClick={() => setFullShown(true)}>
              Загрузить еще
            </Button>
          </div>
        )}
      </div>
    </section>
  );
};

export default BuildingProgress;
